import { Injectable } from '@angular/core';
import { TitleCasePipe } from '@angular/common';
import { HttpErrorResponse } from '@angular/common/http';

import { Actions, createEffect, ofType } from '@ngrx/effects';
import { Store } from '@ngrx/store';
import { ToastrService } from 'ngx-toastr';

import { catchError, map, switchMap, tap, withLatestFrom } from 'rxjs/operators';
import { of } from 'rxjs';

import { AppState } from '../../../state';
import { DashboardCamelCasePipe } from '../../../core/pipes/dashboard-camel-case/dashboard-camel-case.pipe';
import * as interventionModuleActions from './intervention-content-item.actions';
import { InterventionContentItemService } from '../../services/intervention-content-item/intervention-content-item.service';
import { selectContentItemsSortingOrder, selectInterventionContentItems } from './intervention-content-item.selectors';
import {
  getInterventionContentItemSortOrderArray,
  SELECT_LIST_ITEM_TYPE,
} from '../../studies/helpers/intervention-helper';

@Injectable()
export class InterventionContentItemEffects {
  addContentItemToIntervention$ = createEffect(() =>
    this.actions$.pipe(
      ofType(interventionModuleActions.addContentItemToIntervention),
      switchMap(({ studyId, interventionId, itemId, itemType }) => {
        return this.interventionContentItemService
          .addContentItemToIntervention(studyId, interventionId, itemId, itemType)
          .pipe(
            map((interventionContentItem) => {
              const newInterventionContentItemCamelCase = this.dashboardCamelCasePipe.keysToCamelCase(
                interventionContentItem
              );
              return interventionModuleActions.addContentItemToInterventionSuccess({
                studyId,
                interventionId,
                interventionContentItem: newInterventionContentItemCamelCase,
              });
            }),
            catchError((err: HttpErrorResponse) =>
              of(
                interventionModuleActions.addContentItemToInterventionError({
                  err,
                })
              )
            )
          );
      })
    )
  );

  addContentItemToInterventionSuccess$ = createEffect(() =>
    this.actions$.pipe(
      ofType(interventionModuleActions.addContentItemToInterventionSuccess),
      withLatestFrom(
        this.store.select(selectInterventionContentItems),
        this.store.select(selectContentItemsSortingOrder)
      ),
      map(
        ([
          { studyId, interventionId, interventionContentItem },
          interventionContentItems,
          contentItemsSortingOrder,
        ]) => {
          let sortingOrder = [];

          if (contentItemsSortingOrder?.length) {
            sortingOrder = contentItemsSortingOrder?.slice();
            sortingOrder.push({
              type: interventionContentItem?.module
                ? SELECT_LIST_ITEM_TYPE.MODULE
                : SELECT_LIST_ITEM_TYPE.INFORMATIVE_PAGE,
              id: interventionContentItem.id,
            });
          } else {
            sortingOrder = getInterventionContentItemSortOrderArray(interventionContentItems);
            const newItemIndex = sortingOrder.findIndex((sortingOrderItem) => {
              return sortingOrderItem?.id === interventionContentItem?.id;
            });
            if (newItemIndex !== -1) {
              sortingOrder.push(sortingOrder[newItemIndex]);
              sortingOrder.splice(newItemIndex, 1);
            }
          }
          if (interventionContentItem?.module) {
            this.toastrService.success(`${this.titleCase.transform(interventionContentItem?.module?.name)} added to intervention!`);
          } else {
            this.toastrService.success(`Page added to intervention!`);
          }
          return interventionModuleActions.saveContentItemsSortingOrder({ studyId, interventionId, sortingOrder });
        }
      )
    )
  );

  removeContentItemFromIntervention$ = createEffect(() =>
    this.actions$.pipe(
      ofType(interventionModuleActions.removeContentItemFromIntervention),
      switchMap(({ studyId, interventionId, itemId, itemType, interventionContentItemId }) => {
        return this.interventionContentItemService
          .removeContentItemFromIntervention(studyId, interventionId, itemId, itemType)
          .pipe(
            map(() => {
              return interventionModuleActions.removeContentItemFromInterventionSuccess({
                studyId,
                interventionId,
                deletedItemId: interventionContentItemId,
                deletedItemType: itemType,
              });
            }),
            catchError((err: HttpErrorResponse) =>
              of(interventionModuleActions.removeContentItemFromInterventionError({ err }))
            )
          );
      })
    )
  );

  removeContentItemFromInterventionSuccess$ = createEffect(() =>
    this.actions$.pipe(
      ofType(interventionModuleActions.removeContentItemFromInterventionSuccess),
      withLatestFrom(this.store.select(selectInterventionContentItems)),
      map(([{ studyId, interventionId, deletedItemId, deletedItemType }, interventionContentItems]) => {
        const sortingOrder = getInterventionContentItemSortOrderArray(interventionContentItems);
        const deletedItemIndex = sortingOrder.findIndex((sortingOrderItem) => {
          return sortingOrderItem?.id === deletedItemId;
        });
        if (deletedItemIndex !== -1) {
          sortingOrder.splice(deletedItemIndex, 1);
        }

        this.toastrService.success(`${this.titleCase.transform(deletedItemType)} deleted from intervention!`);
        return interventionModuleActions.saveContentItemsSortingOrder({ studyId, interventionId, sortingOrder });
      })
    )
  );

  removeContentItemFromInterventionError$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(interventionModuleActions.removeContentItemFromInterventionError),
        tap(({ err }) => this.toastrService.error(`Unable to delete module from intervention`))
      ),
    { dispatch: false }
  );

  saveContentItemsSortingOrder$ = createEffect(() =>
    this.actions$.pipe(
      ofType(interventionModuleActions.saveContentItemsSortingOrder),
      switchMap(({ studyId, interventionId, sortingOrder }) => {
        return this.interventionContentItemService
          .saveContentItemsSortingOrder(studyId, interventionId, sortingOrder)
          .pipe(
            map((newSortingOrder) => {
              return interventionModuleActions.saveContentItemsSortingOrderSuccess({ newSortingOrder });
            }),
            catchError((err: HttpErrorResponse) =>
              of(
                interventionModuleActions.saveContentItemsSortingOrderError({
                  err,
                })
              )
            )
          );
      })
    )
  );

  constructor(
    private interventionContentItemService: InterventionContentItemService,
    private actions$: Actions,
    private store: Store<AppState>,
    private toastrService: ToastrService,
    private dashboardCamelCasePipe: DashboardCamelCasePipe,
    private titleCase: TitleCasePipe
  ) {}
}
